import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { JobTask } from '@components/job/workProgress/task';
import { sortByProp } from '@components/job/workProgress/helpers';
import { JobTask as JobTaskType } from '@root/globalTypes';
import { getDefaultJobDayKey, parseJobDays } from './helpers';
import { JobStageDaysProps } from './types';
import { JobDayTab } from './tab';
import { JobDaysCalendar } from './calendar';
import { JobDaysMenu } from './menu';

export const JobStageDays = ({ stage, days: data }: JobStageDaysProps) => {
  const days = useMemo(() => parseJobDays(data), [data]);
  const [activeKey, setActiveKey] = useState(() => getDefaultJobDayKey(days));
  const scrollRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const activeDay = days.find((el) => el.key === activeKey);

  const scrollToDay = (key: string, behavior: ScrollBehavior = 'smooth') => {
    const container = scrollRef.current;
    const tab = tabRefs.current.get(key);
    if (!container || !tab) return;
    container.scrollTo({
      left: tab.offsetLeft - (container.clientWidth - tab.clientWidth) / 2,
      behavior,
    });
  };

  const selectDay = (key: string) => {
    setActiveKey(key);
    scrollToDay(key);
  };

  useLayoutEffect(() => {
    scrollToDay(activeKey, 'auto');
  }, []);

  useEffect(() => {
    if (activeDay) return;
    const key = getDefaultJobDayKey(days);
    setActiveKey(key);
    scrollToDay(key, 'auto');
  }, [activeDay, days]);

  return (
    <>
      <div className="col-start-1 col-end-5 flex items-center gap-2 border-y border-neutral-200 bg-neutral-100 px-3 py-1">
        <JobDaysCalendar days={days} activeKey={activeKey} onSelect={selectDay} />
        <div
          ref={scrollRef}
          className="relative min-w-0 flex-1 overflow-x-auto py-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
          <div className="flex w-max gap-1.5 px-1">
            {days.map((day) => (
              <JobDayTab
                key={day.key}
                ref={(el) => {
                  if (el) tabRefs.current.set(day.key, el);
                  else tabRefs.current.delete(day.key);
                }}
                data={day}
                active={day.key === activeKey}
                onClick={() => selectDay(day.key)}
              />
            ))}
          </div>
        </div>
        <JobDaysMenu days={days} activeKey={activeKey} onSelect={selectDay} />
      </div>
      {activeDay &&
        sortByProp<JobTaskType>([...activeDay.tasks], 'position', 'asc').map((task) => (
          <JobTask key={task.id} data={task} date={activeDay.date} stage={stage} />
        ))}
    </>
  );
};
